import { t } from '@trimble/i18n';
import { SnappingCapability } from '@trimble/web3d-capabilities';
import { SnapType } from '@trimble/web3d-providers';
import { JSX } from 'solid-js';
import { BasicButton } from '../toolbar/toolbar-controls/BasicButton';
import { CustomTooltip } from '@tcweb/connect-ui';

export type SnapTypeKind = 'face' | 'line' | 'point' | 'center';

export const BasicSnapping = (props: {
  snappingCapability: SnappingCapability;
  /**
   * Called when the user toggles a snap type. Optional; used by hosts for analytics.
   */
  onSnapToggle?(enabled: boolean, snapType: SnapTypeKind): void;
}): JSX.Element => {
  const faceSnapType = [SnapType.FACE];
  const lineSnapType = [SnapType.LINE];
  const pointSnapType = [
    SnapType.POINT,
    SnapType.CONTROL_POINT,
    SnapType.MIDPOINT,
    SnapType.PERPENDICULAR_POINT,
    SnapType.INTERSECTION_POINT,
  ];
  const centerSnapType = [SnapType.CENTER_LINE, SnapType.CENTER_POINT];

  const isFaceSnapTypeActive =
    // eslint-disable-next-line solid/reactivity
    props.snappingCapability.isSnapTypeActive(faceSnapType);
  const isLineSnapTypeActive =
    // eslint-disable-next-line solid/reactivity
    props.snappingCapability.isSnapTypeActive(lineSnapType);
  const isPointSnapTypeActive =
    // eslint-disable-next-line solid/reactivity
    props.snappingCapability.isSnapTypeActive(pointSnapType);
  const isCenterSnapTypeActive =
    // eslint-disable-next-line solid/reactivity
    props.snappingCapability.isSnapTypeActive(centerSnapType);

  return (
    <div class="flex gap-1">
      <CustomTooltip
        text={t('web3D.toolbar.actions.faceSnapType')}
        position="bottom"
        arrow={true}
      >
        <BasicButton
          data-analytics-id="toggle-face-snap"
          iconName="tc-icon-snap-face"
          action={() => {
            const enabled = !isFaceSnapTypeActive();
            props.snappingCapability.toggleSnapType(enabled, faceSnapType);
            props.onSnapToggle?.(enabled, 'face');
          }}
          pressed={isFaceSnapTypeActive()}
          size="small"
        />
      </CustomTooltip>
      <CustomTooltip
        text={t('web3D.toolbar.actions.lineSnapType')}
        position="bottom"
        arrow={true}
      >
        <BasicButton
          data-analytics-id="toggle-line-snap"
          iconName="tc-icon-snap-line"
          action={() => {
            const enabled = !isLineSnapTypeActive();
            props.snappingCapability.toggleSnapType(enabled, lineSnapType);
            props.onSnapToggle?.(enabled, 'line');
          }}
          pressed={isLineSnapTypeActive()}
          size="small"
        />
      </CustomTooltip>
      <CustomTooltip
        text={t('web3D.toolbar.actions.pointSnapType')}
        position="bottom"
        arrow={true}
      >
        <BasicButton
          data-analytics-id="toggle-point-snap"
          iconName="tc-icon-snap-endpoint"
          action={() => {
            const enabled = !isPointSnapTypeActive();
            props.snappingCapability.toggleSnapType(enabled, pointSnapType);
            props.onSnapToggle?.(enabled, 'point');
          }}
          pressed={isPointSnapTypeActive()}
          size="small"
        />
      </CustomTooltip>
      <CustomTooltip
        text={t('web3D.toolbar.actions.centerSnapType')}
        position="bottom"
        arrow={true}
      >
        <BasicButton
          data-analytics-id="toggle-center-snap"
          iconName="tc-icon-snap-center"
          action={() => {
            const enabled = !isCenterSnapTypeActive();
            props.snappingCapability.toggleSnapType(enabled, centerSnapType);
            props.onSnapToggle?.(enabled, 'center');
          }}
          pressed={isCenterSnapTypeActive()}
          size="small"
        />
      </CustomTooltip>
    </div>
  );
};
